import { KpiTileViewModel } from 'src/app/dashboards/common/items/kpi-tiles/kpi-tiles.component';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContextDatasourceColumn,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardDecoActivitySqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import {
    decoFlowPercentileLabel,
    formatDecoFlowValue,
    getDecoFlowMetric,
} from '../deco-flow.models';

export interface DecoFlowKpiDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/**
 * Deco Flow KPI tiles aggregate a daily series, like Resource Flow. Every statistic states its own
 * complete query so the emitted SQL reads as SQL: on `dashboard_deco_activity` the inflow side is
 * `inflow_delta` (decorations gained) and the outflow side is `new_placed_delta` (decorations
 * placed).
 */
export class DecoFlowKpiDataLoaderFactory extends Factory<
    DataLoader,
    DecoFlowKpiDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DecoFlowKpiDataLoaderFactory';

    getInstance(options: DecoFlowKpiDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const decoSqlMap = BiVizDashboardDecoActivitySqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    decoSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // KPI keeps filters, not chart Split or Group.
                const decoSQL =
                    BiVizDashboardDecoActivitySqlMap.sqlStatementsCompiler.compileSQLStatements(
                        withoutSplitOrBucket(dsColumns.present),
                        queryContext.settings,
                        options
                    );

                const metric = getDecoFlowMetric(queryContext);

                if (
                    metric.statistic === 'percentile' &&
                    !metric.percentiles.length
                ) {
                    throw new Error(
                        'Deco Flow KPI: percentile mode needs at least one selected percentile'
                    );
                }

                // Average and Percentile both offer the DAU population.
                const usesDauPopulation =
                    (metric.statistic === 'average' ||
                        metric.statistic === 'percentile') &&
                    metric.population === 'dau';

                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        withoutSplitOrBucket(
                            usersdashActivityDsColumns.present
                        ),
                        queryContext.settings,
                        options
                    );

                const decoTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    decoSqlMap.dataset,
                    decoSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );
                // DAU metrics report the full active population.
                const populationQuery = `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI affected players - population: ${metric.population}

WITH flow_players AS
(
  SELECT
      ds.player_id
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
  GROUP BY ds.player_id
  HAVING COUNTIF(
      ds.inflow_delta > 0 OR ds.new_placed_delta > 0
  ) > 0
)
, active_users AS
(
  SELECT
      DISTINCT ds.player_id
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersdashActivitySQL.where}
)
SELECT
    SAFE_DIVIDE(
        COUNT(${
            usesDauPopulation
                ? 'active_users.player_id'
                : 'flow_players.player_id'
        })
      , COUNT(active_users.player_id)
    ) AS population_ratio
  , COUNT(${
      usesDauPopulation ? 'active_users.player_id' : 'flow_players.player_id'
  }) AS player_count
  , COUNT(active_users.player_id) AS total_player_count
FROM active_users
LEFT JOIN flow_players USING (player_id)
`;
                const populationResponsePromise = DataLoader.runQuery(
                    populationQuery,
                    options.queryCacheService
                );

                const buildQuery = (): string => {
                    if (metric.statistic === 'total') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: total

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , ROUND(SUM(ds.inflow_delta), 2)     AS inflow
  , ROUND(SUM(ds.new_placed_delta), 2) AS outflow
FROM \`${decoTablePath}\` ds
${decoSQL.join}
${decoSQL.where}
GROUP BY date
ORDER BY date
`;
                    }

                    if (metric.statistic === 'flow records') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: flow records

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , COUNTIF(ds.inflow_delta > 0)     AS inflow
  , COUNTIF(ds.new_placed_delta > 0) AS outflow
FROM \`${decoTablePath}\` ds
${decoSQL.join}
${decoSQL.where}
GROUP BY date
ORDER BY date
`;
                    }

                    if (metric.statistic === 'players') {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: players

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , COUNT(DISTINCT IF(ds.inflow_delta > 0, ds.player_id, NULL))     AS inflow
  , COUNT(DISTINCT IF(ds.new_placed_delta > 0, ds.player_id, NULL)) AS outflow
FROM \`${decoTablePath}\` ds
${decoSQL.join}
${decoSQL.where}
GROUP BY date
ORDER BY date
`;
                    }

                    if (
                        metric.statistic === 'average' &&
                        metric.population === 'deu'
                    ) {
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: average per deu

SELECT
    FORMAT_DATE('%Y-%m-%d', DATE(${decoSQL.dateTrunc()})) AS date
  , ROUND(SAFE_DIVIDE(
        SUM(ds.inflow_delta),
        COUNT(DISTINCT IF(ds.inflow_delta > 0, ds.player_id, NULL))
    ), 2) AS inflow
  , ROUND(SAFE_DIVIDE(
        SUM(ds.new_placed_delta),
        COUNT(DISTINCT IF(ds.new_placed_delta > 0, ds.player_id, NULL))
    ), 2) AS outflow
FROM \`${decoTablePath}\` ds
${decoSQL.join}
${decoSQL.where}
GROUP BY date
ORDER BY date
`;
                    }

                    if (
                        metric.statistic === 'average' &&
                        metric.population === 'dau'
                    ) {
                        // The active population drives the series: a date bucket without any deco flow
                        // still reports its DAU denominator, hence the LEFT JOIN from active_users.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: average per dau

WITH active_users AS
(
  SELECT
      DATE(ds.date) AS date
    , COUNT(DISTINCT ds.player_id) AS population
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersdashActivitySQL.where}
  GROUP BY date
)
, daily_values AS
(
  SELECT
      DATE(${decoSQL.dateTrunc()}) AS date
    , SUM(ds.inflow_delta)     AS inflow
    , SUM(ds.new_placed_delta) AS outflow
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
  GROUP BY date
)
SELECT
    FORMAT_DATE('%Y-%m-%d', active_users.date) AS date
  , ROUND(SAFE_DIVIDE(COALESCE(daily_values.inflow, 0), active_users.population), 2)  AS inflow
  , ROUND(SAFE_DIVIDE(COALESCE(daily_values.outflow, 0), active_users.population), 2) AS outflow
FROM active_users
LEFT JOIN daily_values USING (date)
ORDER BY date
`;
                    }

                    if (
                        metric.statistic === 'percentile' &&
                        metric.population === 'deu'
                    ) {
                        // The percentile answers "the typical player over the applied range", so it is
                        // taken once over the pooled player/date-bucket values instead of averaging
                        // daily percentiles. `deu` counts only players with a positive flow on that
                        // side (APPROX_QUANTILES skips NULL).
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: ${metric.label}

WITH perPlayerDate AS
(
  SELECT
      DATE(${decoSQL.dateTrunc()}) AS date
    , ds.player_id
    , SUM(ds.inflow_delta)     AS inflow
    , SUM(ds.new_placed_delta) AS outflow
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
  GROUP BY date, player_id
)
SELECT
    APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${
        metric.primaryPercentile
    })]   AS inflow
  , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${
      metric.primaryPercentile
  })] AS outflow
FROM perPlayerDate
`;
                    }

                    if (
                        metric.statistic === 'percentile' &&
                        metric.population === 'dau'
                    ) {
                        // `dau` percentiles every active player and reads a missing flow as zero, so the
                        // per-player values are first joined onto the whole active population.
                        return `
-- DataLoaderId: ${this.instanceId}
-- Deco Flow KPI - mode: ${metric.label}

WITH perPlayerDate AS
(
  SELECT
      DATE(${decoSQL.dateTrunc()}) AS date
    , ds.player_id
    , SUM(ds.inflow_delta)     AS inflow
    , SUM(ds.new_placed_delta) AS outflow
  FROM \`${decoTablePath}\` ds
  ${decoSQL.join}
  ${decoSQL.where}
  GROUP BY date, player_id
)
, dauPlayers AS
(
  SELECT
      DATE(${decoSQL.dateTrunc()}) AS date
    , ds.player_id
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersdashActivitySQL.where}
  GROUP BY date, player_id
)
, population AS
(
  SELECT
      COALESCE(perPlayerDate.inflow, 0)  AS inflow
    , COALESCE(perPlayerDate.outflow, 0) AS outflow
  FROM dauPlayers
  LEFT JOIN perPlayerDate USING (date, player_id)
)
SELECT
    APPROX_QUANTILES(inflow, 100)[OFFSET(${
        metric.primaryPercentile
    })]  AS inflow
  , APPROX_QUANTILES(outflow, 100)[OFFSET(${
      metric.primaryPercentile
  })] AS outflow
FROM population
`;
                    }

                    // A new statistic or population must state its own KPI query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Deco Flow KPI: no query defined for statistic '${metric.statistic}' and population '${metric.population}'`
                    );
                };

                const query = buildQuery();

                const [queryResponse, populationResponse] = await Promise.all([
                    DataLoader.runQuery(query, options.queryCacheService),
                    populationResponsePromise,
                ]);
                cancellationToken.throwIfCancellationRequested();

                const rows = [...queryResponse.result];
                let inflow: number;
                let outflow: number;
                if (metric.statistic === 'percentile') {
                    // Already one range-wide value per side; there is no daily series to reduce.
                    const row = rows[0];
                    if (!row) {
                        throw new Error(
                            'Deco Flow KPI: percentile query returned no row'
                        );
                    }
                    inflow = +(row.inflow ?? 0) || 0;
                    outflow = +(row.outflow ?? 0) || 0;
                } else {
                    const dateAggregation =
                        queryContext.settings[
                            QueryContextSettingID.DATE_AGGREGATION
                        ] ?? 'day';
                    XYChartAxis.fillMissingTimeGapRows(
                        rows,
                        queryContext,
                        'date',
                        dateAggregation,
                        { dimensionKeys: [] }
                    );
                    inflow = rangeValue(
                        rows.map((row) => +(row.inflow ?? 0) || 0),
                        metric.rangeAggregation
                    );
                    outflow = rangeValue(
                        rows.map((row) => +(row.outflow ?? 0) || 0),
                        metric.rangeAggregation
                    );
                }
                const suffix =
                    metric.statistic === 'percentile'
                        ? ` (${decoFlowPercentileLabel(metric.primaryPercentile)})`
                        : '';
                const valueFormatter =
                    metric.rangeAggregation === 'sum'
                        ? undefined
                        : formatDecoFlowValue;
                const net = inflow - outflow;
                const populationRatio =
                    +(populationResponse.result[0]?.population_ratio ?? 0) || 0;
                const playerCount =
                    +(populationResponse.result[0]?.player_count ?? 0) || 0;
                const totalPlayerCount =
                    +(populationResponse.result[0]?.total_player_count ?? 0) ||
                    0;
                const data: KpiTileViewModel[] = [
                    {
                        label: `Gained${suffix}`,
                        value: inflow,
                        unit: metric.unit,
                        valueFormatter,
                        tone: 'positive',
                        indicator: 'up',
                    },
                    {
                        label: `Placed${suffix}`,
                        value: outflow,
                        unit: metric.unit,
                        valueFormatter,
                        tone: 'negative',
                        indicator: 'down',
                    },
                    {
                        label: `Net${suffix}`,
                        value: net,
                        unit: metric.unit,
                        valueFormatter,
                        tone: net >= 0 ? 'positive' : 'negative',
                        indicator: net >= 0 ? 'up' : 'down',
                    },
                    {
                        label: 'Affected players',
                        value: populationRatio,
                        unit: `${playerCount.toLocaleString(
                            'en-US'
                        )} of ${totalPlayerCount.toLocaleString(
                            'en-US'
                        )} players`,
                        tone: 'neutral',
                        indicator: 'none',
                        valueFormatter: (value) =>
                            `${(value * 100).toFixed(1)}%`,
                    },
                ];
                this.latestData = data;
                this.emitter.emit({
                    data: this.latestData,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: `${query}\n\n${populationQuery}`,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

/** KPI queries keep filters but intentionally never inherit chart Split or Group dimensions. */
function withoutSplitOrBucket(
    columns: IQueryContextDatasourceColumn[]
): IQueryContextDatasourceColumn[] {
    return columns.map((column) => ({
        ...column,
        state: {
            ...column.state,
            isSplitApplied: false,
            isBucketApplied: false,
        },
    }));
}

function rangeValue(values: number[], aggregation: 'sum' | 'mean'): number {
    if (!values.length) {
        return 0;
    }
    const sum = values.reduce((total, value) => total + value, 0);
    return aggregation === 'sum' ? sum : sum / values.length;
}
